import { FC, useEffect, useRef, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { Icon } from 'src/components';
import * as S from './styles';

const msgs = defineMessages({
  play: { id: 'blob_eos_play_clip', defaultMessage: 'Play video' },
  pause: { id: 'blob_eos_pause_clip', defaultMessage: 'Pause video' },
  unmute: { id: 'blob_eos_unmute_clip', defaultMessage: 'Turn on sound' },
});

export type ClipAutoplayResult = 'sound' | 'muted' | 'blocked';
export type ClipPlayTrigger = 'play' | 'replay' | 'unmute';

type CelebrationClipProps = {
  src: string;
  onAutoplay?: (result: ClipAutoplayResult) => void;
  onPlay?: (trigger: ClipPlayTrigger) => void;
};

export const CelebrationClip: FC<CelebrationClipProps> = ({ src, onAutoplay, onPlay }) => {
  const intl = useIntl();
  const videoRef = useRef<HTMLVideoElement>(null);
  const onAutoplayRef = useRef(onAutoplay);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);

  useEffect(() => {
    onAutoplayRef.current = onAutoplay;
  }, [onAutoplay]);

  /* Plays once on arrival. Browsers that block autoplay with sound still allow it muted,
   * in which case a tap turns the sound on. */
  useEffect(() => {
    const video = videoRef.current as HTMLVideoElement;

    const autoplay = async (): Promise<ClipAutoplayResult> => {
      try {
        await video.play();
        return 'sound';
      } catch {
        video.muted = true;
        setIsMuted(true);
      }

      try {
        await video.play();
        return 'muted';
      } catch {
        return 'blocked';
      }
    };

    void (async () => {
      const result = await autoplay();
      onAutoplayRef.current?.(result);
    })();
  }, []);

  const handleClick = () => {
    const video = videoRef.current as HTMLVideoElement;

    if (video.paused || video.muted) {
      if (video.muted) {
        onPlay?.('unmute');
      } else {
        onPlay?.(video.ended ? 'replay' : 'play');
      }
      video.muted = false;
      setIsMuted(false);
      video.play().catch(() => {});
    } else {
      video.pause();
    }
  };

  const getLabel = () => {
    if (!isPlaying) return msgs.play;
    return isMuted ? msgs.unmute : msgs.pause;
  };

  return (
    <S.Circle
      type="button"
      onClick={handleClick}
      aria-label={intl.formatMessage(getLabel())}
      data-testid="blob-eos-clip"
    >
      <S.Video
        ref={videoRef}
        src={`${src}#t=0.1`}
        preload="auto"
        playsInline
        data-testid="blob-eos-clip-video"
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        onEnded={() => setIsPlaying(false)}
      />
      {!isPlaying && (
        <S.PlayIcon>
          <Icon name="playFilled" />
        </S.PlayIcon>
      )}
    </S.Circle>
  );
};
